import { useState, useEffect } from "react";
import clsx from 'clsx'
import { useIntl } from 'react-intl';
import { PageTitle, useLayout } from "../../../_metronic/layout/core";
import { DefaultTitle } from "../../../_metronic/layout/components/header/page-title/DefaultTitle";
import { IUsuarios } from "../../interfaces/IUsuarios";
import UsuariosService from "../../services/UsuariosService";
import UsuariosModal from "./UsuariosModal";
import { AcoesStatusUsuario, BadgeStatusUsuario, FiltroStatusUsuario, FiltroStatusUsuarioBotoes, HistoricoStatusUsuarioModal, alterarStatusUsuario, usuarioEstaAtivo } from "../usuarios/StatusUsuario";
import UsuariosTabela from "./UsuariosTabela";
import { useAuth } from "../../contexts/AuthProvider";

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

function Usuarios() {

    const intl = useIntl();
    const { usuario: usuarioLogado } = useAuth();
    const { classes } = useLayout();
    const { t } = useTranslation();
    const [usuarios, setUsuarios] = useState<IUsuarios[]>([]);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [usuario, setUsuario] = useState<IUsuarios>({} as IUsuarios);
    const [modalTitle, setModalTitle] = useState<string>('');
    const [paginaCarregada, setPaginaCarregada] = useState<boolean>(false);
    const [filtro, setFiltro] = useState<string>('');
    // DEVN3-780: abre só com os ativos; inativos aparecem no filtro e podem ser reativados por lá.
    const [filtroStatus, setFiltroStatus] = useState<FiltroStatusUsuario>('ativos');
    const [usuarioHistorico, setUsuarioHistorico] = useState<IUsuarios | undefined>(undefined);
    const columns = [
        {
            name: t('Nome'),
            selector: (row: IUsuarios) => row['nome'],
            sortable: true,
            wrap: true,
            cell: (row: IUsuarios) => (
                <span style={{ color: usuarioEstaAtivo(row) ? undefined : '#A3A3A3', fontStyle: usuarioEstaAtivo(row) ? undefined : 'italic' }}>
                    {row.nome}
                </span>
            )
        },
        {
            name: t('Email'),
            selector: (row: IUsuarios) => row['email'],
            sortable: true,
            wrap: true
        },
        {
            name: t('Nome de Usuário'),
            selector: (row: IUsuarios) => row['nomeUsuario'],
            sortable: true,
            wrap: true
        },
        {
            name: t('Perfil'),
            selector: (row: IUsuarios) => row['perfil'],
            sortable: true,
            wrap: true
        },
        {
            name: t('Status'),
            cell: (row: IUsuarios) => <BadgeStatusUsuario item={row} />,
            wrap: true
        },
        {
            name: t('Ações'),
            cell: (row: IUsuarios) => <AcoesUsuarios editarUsuario={(usuario: IUsuarios) => {
                setModalTitle(t('Editar Usuário'));
                setUsuario(usuario);
                toggleModal();
            }} item={row} />,
            wrap: true
        }
    ];

    useEffect(() => {
        const content = document.getElementById('kt_content_container')
        const estilo = (content?.getAttribute('style') ?? "") + 'padding: 10px';

        content?.setAttribute('style', estilo);

        setTimeout(() => {
            carregarUsuarios();
        }, 100)
    }, []);

    async function carregarUsuarios() {
        if (usuarioLogado?.clienteID) {
            setPaginaCarregada(false);
            await UsuariosService.obterTodosPorClienteID(usuarioLogado.clienteID, x => setUsuarios(x));
            setPaginaCarregada(true);
        }
    }

    function toggleModal(loadItens?: boolean): void {
        if (loadItens) {
            carregarUsuarios();
        }

        setShowModal(!showModal);

        if (showModal) {
            setTimeout(() => {
                setUsuario({} as IUsuarios)
            }, 300)
        }
    }

    function AcoesUsuarios({ item, editarUsuario }: { item: IUsuarios, editarUsuario: (Usuario: IUsuarios) => void }) {
        return <div data-tag="allowRowEvents" className="d-flex align-items-center gap-3">
            <AcoesStatusUsuario
                item={item}
                onAlterarStatus={u => alterarStatusUsuario(u, t, () => carregarUsuarios())}
                onHistorico={setUsuarioHistorico}
            />
            <i onClick={() => editarUsuario(item)} title={t('Editar')} className="fas fa-edit fs-2" style={{ color: '#5E6397', fontWeight: 'normal', cursor: 'pointer' }} />
        </div>
    }

    const usuariosFiltrados = usuarios.filter((i) => {
        if (filtroStatus === 'ativos' && !usuarioEstaAtivo(i)) return false;
        if (filtroStatus === 'inativos' && usuarioEstaAtivo(i)) return false;
        if (filtro) {
            const value = `${i.nome} - ${i.celular} - ${i.email} - ${i.perfil}`.toLowerCase();
            return value.includes(filtro.toLowerCase());
        }
        return true;
    });

    return <>
        <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: t('MENU.USERS') })}</PageTitle>
        <div className='toolbar' id='kt_toolbar'>
            <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                <DefaultTitle />
                <UsuariosModal usuario={usuario} title={modalTitle} show={showModal} toggleModal={toggleModal} />
                <div className='d-flex align-items-center py-1'>
                    <button onClick={() => {
                        setModalTitle(t('Cadastrar Usuário'));
                        setUsuario({ id: 0 } as IUsuarios);
                        setShowModal(true);
                    }} className='btn btn-sm btn-primario'>
                        <i className="icon-lg text-white fas fa-plus"></i>
                        <span style={{ verticalAlign: 'middle' }}> {t("USUÁRIO")} </span>
                    </button>
                </div>
            </div>
        </div>
        <HistoricoStatusUsuarioModal usuario={usuarioHistorico} onClose={() => setUsuarioHistorico(undefined)} />
        <div className="row g-3 mb-4">
            <div className="col-12 col-md">
                <input type='text' className="form-control" placeholder={t('Filtrar por nome, e-mail, telefone, perfil')} onChange={(e) => setFiltro(e.target.value)} />
            </div>
            <div className="col-12 col-md-auto d-flex align-items-center">
                <FiltroStatusUsuarioBotoes valor={filtroStatus} onChange={setFiltroStatus} />
            </div>
        </div>

        {
            paginaCarregada ? <UsuariosTabela colunas={columns} itens={usuariosFiltrados} /> : <p>{t("Carregando...")}</p>
        }
    </>
};

export default Usuarios;